import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { ObjectType } from '~/static/enums/object-type'
import { EditorObject, LayoutContext, NamingContext } from '@/utils/editor/object.class'
import { Figure, figures } from '~/static/enums/figures'
import { FillType } from '~/static/enums/components/color-input'
import { ActualVersion, ActualVersionNumber } from '~/utils/editor/versioning/svg-figure'
import { assignNewColorAndAlpha, makeFabricGradient } from '~/utils/ColorHelper'
import { makeShadow } from '~/utils/editor/helpers/Helpers'
import { AnyActualProp } from '../versioning/migrators'

const DefaultWidth = 116
const DefaultHeight = 116

export class SvgFigure extends EditorObject {
  title: string = ''
  left: number = 0
  top: number = 0
  angle: number = 0
  flipX: boolean = false
  flipY: boolean = false
  width: number = DefaultWidth
  height: number = DefaultHeight
  borderRadius: number = 0
  hasBackground: boolean = true
  colorType: FillType = FillType.Solid
  color: FabricGradientOptions | string = 'rgba(69,69,69,1)'
  opacity: number = 100
  border: number = 1
  borderColor: string = 'rgba(0,0,0,1)'
  borderOpacity: number = 100
  borderDash: boolean = false
  borderExists: boolean = false
  shadow: boolean = false
  shadowColor: string = 'rgba(0,0,0,1)'
  shadowOpacity: number = 100
  shadowX: number = 0
  shadowY: number = 4
  shadowBlur: number = 4
  figureIndex!: number

  svg: string = '<svg></svg>'
  figure: Figure

  constructor(props: ActualVersion) {
    super(ObjectType.SvgFigure)
    this.assignProperties(props)
    this.figure = figures[this.figureIndex]
    this.svg = this.figure.svg
  }

  load(): Promise<void> {
    return new Promise((resolve, reject) => {
      fabric.loadSVGFromString(figures[this.figureIndex].svg)
        .then((result) => {
          this.setObject(fabric.util.groupSVGElements(
            result.objects.filter((item) => { return item !== null }) as fabricTypes.FabricObject[],
            result.options,
          ))
          this.object.objectCaching = false
          resolve()
        })
        .catch((error) => {
          console.log(error)
          reject('Не удалось распознать фигуру')
        })
    })
  }

  applyInitialLayout(context: LayoutContext): void {
    if (this.figure.defaultParams) {
      this.assignProperties(this.figure.defaultParams)
    }
    this.width = this.width / context.zoom
    this.height = this.height / context.zoom
  }

  applyDefaultName(context: NamingContext): void {
    if (!this.title || this.title.length === 0) {
      this.title = 'Фигура ' + context.getNextIndex(ObjectType.SvgFigure)
    }
  }

  protected assignProperties(props: ActualVersion): void {
    super.assignProperties(props)

    const color = assignNewColorAndAlpha(this.colorType, this.color, this.opacity, props.color, props.opacity)
    this.color = color.color
    this.opacity = color.opacity

    const borderColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.borderColor,
      this.borderOpacity,
      props.borderColor,
      props.borderOpacity,
    )
    this.borderColor = <string>borderColor.color
    this.borderOpacity = borderColor.opacity

    const shadowColor = assignNewColorAndAlpha(
      FillType.Solid,
      this.shadowColor,
      this.shadowOpacity,
      props.shadowColor,
      props.shadowOpacity,
    )
    this.shadowColor = <string>shadowColor.color
    this.shadowOpacity = shadowColor.opacity
  }

  async set(props: ActualVersion): Promise<void> {
    this.assignProperties(props)
    return this.applyProps()
  }

  protected applyProps(): Promise<void> {
    super.applyProps()

    this.object.set({
      fill: this.hasBackground ?
        (typeof this.color === 'string') ?
          this.color :
          makeFabricGradient(this.object.width ?? DefaultWidth, this.object.height ?? DefaultHeight, this.color) :
        undefined,
      strokeWidth: this.borderExists ? this.border : 0,
      stroke: this.borderColor,
      strokeDashArray: this.borderDash ? [ 10 ] : undefined,
      shadow: makeShadow(this),
      scaleX: this.width / (this.object.width ?? DefaultWidth),
      scaleY: this.height / (this.object.height ?? DefaultHeight),
      // Important for node render: if this isn't set, shadow ignores transparency
      objectCaching: true,
      strokeUniform: true,
    })

    if (this.figure.roundBordersFunction !== undefined) {
      const newPath = new fabric.Path(
        this.figure.roundBordersFunction(this.object.height ?? this.height, this.borderRadius),
      );
      (<fabricTypes.Path><unknown>this.object).path = newPath.path
      this.object.dirty = true
    }

    this.onScaling()
    return Promise.resolve()
  }

  onScaling(): boolean {
    let rx = 0
    let ry = 0
    const realBorderRadius = this.figure.canRoundBorder ?
      this.borderRadius :
      (this.figure.defaultParams?.borderRadius ?? 0)
    if (realBorderRadius !== 0) {
      rx = realBorderRadius / ((this.object.scaleX ?? 1) * (this.object.group?.scaleX ?? 1))
      ry = realBorderRadius / ((this.object.scaleY ?? 1) * (this.object.group?.scaleX ?? 1))
    }

    this.object.set({ rx, ry })
    this.object.setCoords()
    return true
  }

  afterScaling(): boolean {
    this.width = parseInt('' + this.getRealObjectWidth())
    this.height = parseInt('' + this.getRealObjectHeight())
    return false
  }

  toObject(): object {
    const data = super.toObject()
    data.version = ActualVersionNumber

    if ('figure' in data) {
      delete data.figure
    }
    if ('svg' in data) {
      delete data.svg
    }

    return data
  }

  getCanvasManagedProperties(): AnyActualProp {
    return Object.assign(super.getCanvasManagedProperties(), {
      width: this.width,
      height: this.height,
    })
  }

  getRealObjectWidth() {
    return (this.object.scaleX ?? 1) * (this.object.group?.scaleX ?? 1) * (<number>this.object.width)
  }

  getRealObjectHeight() {
    return (this.object.scaleY ?? 1) * (this.object.group?.scaleY ?? 1) * (<number>this.object.height)
  }

  get name(): string {
    return this.title ?? 'Фигура'
  }

  get bodyColour(): Rgba {
    if (typeof this.color === 'string') {
      return parseRgba(this.color)
    }
    if (this.color.colorStops === undefined) {
      return { r: 0, g: 0, b: 0, a: 1 }
    }
    let prevOffset = 0
    let maxSize = 0
    let maxColour = 'rgba(0,0,0,1)'
    for (const colorStop of this.color.colorStops.sort((a, b) => { return a.offset - b.offset })) {
      const size = colorStop.offset - prevOffset
      if (size > maxSize) {
        maxSize = size
        maxColour = colorStop.color
      }
      prevOffset = colorStop.offset
    }
    return parseRgba(maxColour)
  }
}
